import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { Button, Segment } from "semantic-ui-react";
import { toast } from "../..";
import { UserRole } from "../../actions/authentificationActions";
import { IStatistics, statisticsInit } from "../../models/statistics";
import { agent } from "../../api/agent";
import { AppState } from "../../store/configureStore";
import { StatisticsTable } from "./StatisticsTable";
import { CalendarInputDouble } from "../../components/CalendarInput/CalendarInputDouble";
import {
    formatDateRange,
    formatDateRangeDisplay,
} from "../../components/CalendarInput/dateHelpers";
import { constructDate } from "../../models/common";
import { Doughnut } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend, Title } from "chart.js";
import "./Statistics.css";

Chart.register(ArcElement, Tooltip, Legend, Title);

export const Statistics = () => {
    const user = useSelector((state: AppState) => state.user);
    const [statistics, setStatistics] = useState<IStatistics[]>([]);
    const [currentActiveCertificates, setCurrentActiveCertificates] = useState<IStatistics>();
    const [dates, setDates] = useState<Date[]>([]);
    const [exporting, setExporting] = useState(false);
    const intl = useIntl();
    const ucqKeys = useRef(Object.keys(statisticsInit).filter(x => x.includes("ucq")));
    const [ageDistribution, setAgeDistribution] = useState<Record<string, number>>({});

    const changePeriod = (e: any, manualInput: boolean) => {
        !manualInput ? setDates(formatDateRange(e)) : setDates(e);
    };

    const fetchCurrentActiveCertificates = useCallback(async () => {
        try {
            const res = await agent.Statistics.getCurrentActiveCertificates();
            if (!user.ecdbConnection) {
                ucqKeys.current.forEach(x => delete res[x]);
            }
            setCurrentActiveCertificates(res);
            setStatistics([res]);
        } catch {
            toast(intl.formatMessage({ id: "unableToFetchStatistics" }), false);
        }
    }, [intl, ucqKeys, user.ecdbConnection]);

    const fetchStatistics = useCallback(async () => {
        await new Promise(r => setTimeout(r, 100));
        try {
            const startDate = constructDate(dates[0]).replace(/\//g, "-");
            const endDate = constructDate(dates[1]).replace(/\//g, "-");
            const res = await agent.Statistics.get(startDate, endDate);
            if (res && !user.ecdbConnection) {
                ucqKeys.current.forEach(x => {
                    if (res.issuedCertificates) delete res.issuedCertificates[x];
                    if (res.firstIssueCertificates) delete res.firstIssueCertificates[x];
                    if (res.expiredCertificates) delete res.expiredCertificates[x];
                    if (res.suspendedCertificates) delete res.suspendedCertificates[x];
                    if (res.withdrawnCertificates) delete res.withdrawnCertificates[x];
                });
            }
            setStatistics([
                res.issuedCertificates,
                res.firstIssueCertificates,
                res.expiredCertificates,
                res.suspendedCertificates,
                res.withdrawnCertificates,
            ]);
            if (currentActiveCertificates) {
                setStatistics(prev => {
                    if (prev) {
                        return [currentActiveCertificates, ...prev];
                    }
                    return prev;
                });
            }
        } catch (err) {
            console.error(err);
            toast(intl.formatMessage({ id: "unableToFetchStatistics" }), false);
        }
    }, [dates, user.ecdbConnection, intl]);

    const exportStatistics = async () => {
        setExporting(true);
        try {
            const startDate = constructDate(dates[0]);
            const endDate = constructDate(dates[1]);
            await agent.Statistics.export(startDate, endDate);
        } catch {
            toast(intl.formatMessage({ id: "failedToExportStatistics" }), false);
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchCurrentActiveCertificates();
    }, [fetchCurrentActiveCertificates]);

    useEffect(() => {
        dates.length > 0 && fetchStatistics();
    }, [fetchStatistics, dates]);

    const fetchAgeDistribution = useCallback(async () => {
        try {
            let res;
            if (dates.length > 0) {
                const startDate = constructDate(dates[0]).replace(/\//g, "-");
                const endDate = constructDate(dates[1]).replace(/\//g, "-");
                res = await agent.Statistics.getAgeDistribution(startDate, endDate);
            } else {
                res = await agent.Statistics.getAgeDistribution();
            }
            setAgeDistribution(res);
        } catch (err) {
            console.error(err);
            toast(intl.formatMessage({ id: "unableToFetchStatistics" }), false);
        }
    }, [dates, intl]);

    useEffect(() => {
        fetchAgeDistribution();
    }, [fetchAgeDistribution]);

    return user.role !== UserRole.PowerUser && user.role !== UserRole.OfficeUser ? (
        <Navigate to="/unauthorized" />
    ) : (
        <div className="statistics-page-container">
            <div className="container-header">
                <h1>{intl.formatMessage({ id: "statistics" })}</h1>
                <Button
                    primary
                    content={intl.formatMessage({ id: "exportStatistics" })}
                    disabled={exporting || !statistics || dates.length === 0}
                    loading={exporting}
                    onClick={() => exportStatistics()}
                />
            </div>
            <div className="statistics-container">
                <Segment className="grey-segment search-segment">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "period" })}</label>
                        <CalendarInputDouble
                            dates={dates}
                            handleDateChange={(e: any, manualInput: boolean) =>
                                changePeriod(e, manualInput)
                            }
                        />
                    </section>
                </Segment>

                {statistics.length > 0 && (
                    <StatisticsTable
                        title={
                            statistics.length > 1
                                ? intl.formatMessage({ id: "allCertificates" })
                                : intl.formatMessage({ id: "currentActiveCertificates" })
                        }
                        statistics={statistics}
                        period={dates.length > 0 ? formatDateRangeDisplay(dates) : ""}
                    />
                )}
            </div>
            <div className="pie-chart">
                <Doughnut
                    data={{
                        labels: Object.keys(ageDistribution),
                        datasets: [
                            {
                                label: "Age distribution",
                                data: Object.values(ageDistribution),
                                backgroundColor: [
                                    "rgb(255, 99, 132)",
                                    "rgb(54, 162, 235)",
                                    "rgb(255, 205, 86)",
                                    "rgb(75, 192, 192)",
                                    "rgb(153, 102, 255)",
                                    "rgb(255, 159, 64)",
                                    "rgb(255, 30, 64)",
                                ],
                                hoverOffset: 4,
                            },
                        ],
                    }}
                    // add title and legend to the right
                    options={{
                        responsive: true,
                        plugins: {
                            title: {
                                display: true,
                                text: intl.formatMessage({ id: "ageDistribution" }),
                                font: {
                                    size: 18,
                                },
                            },
                            legend: {
                                position: "right",
                            },
                        },
                    }}
                />
            </div>
        </div>
    );
};
